/**
 * @fileoverview Consent question component for rendering consent questions
 *
 * @example
 * // Usage in component:
 * import { ConsentQuestion } from './Consent'
 *
 * <ConsentQuestion onSubmit={onSubmit} onExit={onExit} question={question} />
 */

import { Instructions } from './sections/Instructions'

import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { ModalContext } from '../../ModalContext'
import { Icon } from '../Icon'
import { NavigationButton } from '../NavigationButton'
import { Text } from '../typography/Typography'
import css from './Checkbox.module.css'
import { PromptModalContent } from './PromptModalContent'
import { Downloadable } from './sections/Downloadable'
import { List } from './sections/List'
import { Title } from './sections/Title'

/**
 * @function ConsentQuestion
 * @description Component for rendering consent questions with checkboxes and navigation
 * @param {Novoic.CheckboxQuestionProps} props - The props for the component
 * @returns {JSX.Element} The ConsentQuestion component
 */
export function ConsentQuestion({ onSubmit, onExit, question }: Novoic.CheckboxQuestionProps) {
    const modal = useContext(ModalContext)
    const { t } = useTranslation()
    const { id, question: body } = question
    const { title, accent, instructions, files, icon, list, subtext, checkboxes, canExit } = body

    const state = checkboxes.map((checkbox) => ({
        ...checkbox,
        value: checkbox.default,
        pristine: true,
    }))

    return (
        <div className={css.container}>
            <Icon name={icon} />
            <Title accent={accent}>{title}</Title>
            {subtext && <Text>{subtext}</Text>}
            {instructions && <Instructions instructions={instructions} />}
            {list && <List list={list} />}
            {files && <Downloadable list={files} />}

            <div className={css.button_container}>
                {canExit && (
                    <NavigationButton
                        type="cancel"
                        label={t('buttons.consent.confirmLater')}
                        onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                            const target = e.target as HTMLButtonElement
                            target.blur()

                            modal.open({
                                render: ({ onConfirm, onCancel }) => (
                                    <PromptModalContent
                                        text={t('modals.exit.text')}
                                        caption={t('modals.exit.caption')}
                                        confirmText={t('modals.exit.buttons.confirm')}
                                        cancelText={t('modals.exit.buttons.cancel')}
                                        onCancel={onCancel}
                                        onConfirm={async () => {
                                            onConfirm()
                                            await onExit()
                                        }}
                                    />
                                ),
                            })
                            return Promise.resolve()
                        }}
                    />
                )}
                <NavigationButton
                    type="confirm"
                    label={body.button}
                    onClick={() =>
                        onSubmit(id, {
                            checkboxes: state.reduce((acc, checkbox) => {
                                acc[checkbox.name] = checkbox.value
                                return acc
                            }, {} as Record<string, boolean>),
                        })
                    }
                />
            </div>
        </div>
    )
}
